import React, { Suspense, Component, ErrorInfo, memo, ComponentType, lazy } from 'react';

// ================================
// TYPES
// ================================
export type WidgetType =
  | 'header'
  | 'headerWeb'
  | 'headerWebEndUser'
  | 'headerWebAdmin'
  | 'headerWebApiKeys'
  | 'headerSpacer'
  | 'login'
  | 'web-login'
  | 'forgot-password-mobile'
  | 'forgot-password'
  | 'register'
  | 'accountCreation-web'
  | 'RegisterByInvite'
  | 'floating-language-selector'
  | 'map'
  | 'sitePaymentsTable'
  | 'paymentsTable'
  | 'memberSitesList'
  | 'siteMembersList'
  | 'statsOverview'
  | 'statsOverviewWeb'
  | 'card'
  | 'CommunityDetailsParameters'
  | 'communityPaymentsList'
  | 'MemberDetailsParameters'
  | 'SiteDetailsParameters'
  | 'graphs'
  | 'table'
  | 'communitiesTable'
  | 'kwickCommunitiesTable'
  | 'membersTable'
  | 'adminUsersInvitedTable'
  | 'adminUsersActiveTable'
  | 'ApiKeysTable'
  | 'UsersAuditTable'
  | 'communityStats'
  | 'areachart'
  | 'areaChart'
  | 'combinedGraph'
  | 'batteryChartMobile'
  | 'batteryChart'
  | 'horizontalChart'
  | 'horizontalChartDouble'
  | 'footer'
  | 'endUserNav'
  | 'rowsWrapper'
  | 'pull-to-refresh'
  | 'parametersDetails';

export interface BaseWidgetConfig {
  id: string;
  type: WidgetType;
  className?: string;
  data?: any;
  widgetsData?: any;
  showInspector?: (widget: BaseWidgetConfig) => void;
  isAndroid?: boolean;
  isIPhone16OrDynamicIslandDevice?: boolean;
}

export interface LayoutConfig {
  marginTop?: string;
}

export interface ComponentRegistryEntry {
  component: ComponentType<any>;
  lazy?: boolean;
  fallback?: ComponentType<any>;
  layoutConfig?: (props: BaseWidgetConfig) => LayoutConfig;
  data?: (props: BaseWidgetConfig) => any;
  type?: string;
}

export type ComponentRegistry = Record<WidgetType, ComponentRegistryEntry>;

export interface ComponentDependencies {
  // From @flexo/molecules
  DetailsList: ComponentType<any>;
  PaymentsList: ComponentType<any>;
  FloatingLanguageSelector: ComponentType<any>;

  // From @flexo/organisms
  Footer: ComponentType<any>;
  EndUserNav: ComponentType<any>;
  StatsOverview: ComponentType<any>;
  HorizontalWrapper: ComponentType<any>;
  BatteryChart: ComponentType<any>;
  CommunityStats: ComponentType<any>;
  GraphContainer: ComponentType<any>;
  Header: ComponentType<any>;
  HeaderWeb: ComponentType<any>;
  HeaderWebEndUser: ComponentType<any>;
  HeaderWebApiKeys: ComponentType<any>;
  RegisterOOBcode: ComponentType<any>;
  AccountCreationWeb: ComponentType<any>;
  InformationCard: ComponentType<any>;
  ParametersDetails: ComponentType<any>;
  RowsWrapper: ComponentType<any>;

  // From @flexo/atoms
  HeaderSpacer: ComponentType<any>;
  GenericTable: ComponentType<any>;
  CommunitiesTable: ComponentType<any>;
  KwickCommunitiesTable: ComponentType<any>;
  MembersTable: ComponentType<any>;
  AdminUsersInvitedTable: ComponentType<any>;
  AdminUsersActiveTable: ComponentType<any>;
  PaymentsTable: ComponentType<any>;
  SitePaymentsTable: ComponentType<any>;
  EsriMapComponent: ComponentType<any>;
  ApiKeysTable: ComponentType<any>;
  UsersAuditTable: ComponentType<any>;

  // From @flexo/templates
  ForgotPasswordMobile: ComponentType<any>;
  ForgotPasswordWeb: ComponentType<any>;
  ApiLogIn: ComponentType<any>;
  WebLogIn: ComponentType<any>;
  RegisterByInvite: ComponentType<any>;
}

// ================================
// ERROR BOUNDARY
// ================================
class ComponentErrorBoundary extends Component<
  {
    children: React.ReactNode;
    fallback?: ComponentType<{ error: Error }>;
    widgetInfo?: { type?: string; id?: string; widgetName?: string; className?: string; };
  },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { widgetInfo } = this.props;
    console.error('Component Factory Error:', {
      error,
      errorInfo,
      widget: widgetInfo
    });
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback;
      const { widgetInfo } = this.props;

      if (FallbackComponent && this.state.error) {
        return <FallbackComponent error={this.state.error} />;
      }

      return (
        <div style={{
          padding: '1rem',
          border: '2px solid #ff6b6b',
          borderRadius: '4px',
          backgroundColor: '#fff5f5'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#d63031' }}>
            Widget Error
          </h3>

          {widgetInfo && (
            <div style={{
              marginBottom: '0.75rem',
              padding: '0.5rem',
              backgroundColor: '#ffe0e0',
              borderRadius: '3px',
              fontSize: '0.875rem'
            }}>
              <strong>Widget Details:</strong><br />
              {widgetInfo.type && <span>Type: <code>{widgetInfo.type}</code><br /></span>}
              {widgetInfo.id && <span>ID: <code>{widgetInfo.id}</code><br /></span>}
              {widgetInfo.widgetName && <span>Name: <code>{widgetInfo.widgetName}</code><br /></span>}
              {widgetInfo.className && <span>Class: <code>{widgetInfo.className}</code></span>}
            </div>
          )}

          <p style={{ margin: '0', fontSize: '0.875rem' }}>
            <strong>Error:</strong> {this.state.error?.message || 'Unknown error occurred'}
          </p>

          <details style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
            <summary style={{ cursor: 'pointer', color: '#636e72' }}>
              Show technical details
            </summary>
            <pre style={{
              marginTop: '0.5rem',
              padding: '0.5rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '3px',
              overflow: 'auto',
              maxHeight: '150px'
            }}>
              {this.state.error?.stack}
            </pre>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

// ================================
// UTILITIES
// ================================
const createMemoizedComponent = <T extends object>(
  Component: React.ComponentType<T>,
  displayName?: string
) => {
  const MemoizedComponent = memo(Component, (prevProps, nextProps) => {
    const prevKeys = Object.keys(prevProps);
    const nextKeys = Object.keys(nextProps);

    if (prevKeys.length !== nextKeys.length) return false;

    return prevKeys.every(key => {
      const prevValue = prevProps[key as keyof T];
      const nextValue = nextProps[key as keyof T];

      if (typeof prevValue === 'object' && prevValue !== null) {
        // @MELINA: you can use lodash isEqual to compare objects now
        return JSON.stringify(prevValue) === JSON.stringify(nextValue);
      }

      return prevValue === nextValue;
    });
  });

  if (displayName) {
    MemoizedComponent['displayName'] = `Memoized(${displayName || 'unknown'})`;
  }

  return MemoizedComponent;
};

// Cache memoized wrappers to preserve component identity across renders
// and prevent unnecessary unmounts/remounts when parents re-render.
const memoizedWrapperCache: WeakMap<any, any> = new WeakMap();

const getCachedMemoizedComponent = <T extends object>(
  Component: React.ComponentType<T>,
  displayName?: string
): React.ComponentType<T> => {
  const cached = memoizedWrapperCache.get(Component);
  if (cached) {
    return (cached as unknown) as React.ComponentType<T>;
  }
  const wrapped = createMemoizedComponent(Component, displayName);
  memoizedWrapperCache.set(Component, wrapped);
  return (wrapped as unknown) as React.ComponentType<T>;
};

const generateStableKey = (config: BaseWidgetConfig): string => {
  // Keep the key minimal and stable to avoid unnecessary remounts
  // across layout/style changes. Only use type + id.
  return `widget-${config.type}-${config.id}`;
};

const calculateMarginTop = (props: BaseWidgetConfig): LayoutConfig => {
  if (!props.className?.includes('customMargin')) return {};
  if (props.isAndroid) return { marginTop: '10.5em' };
  return !props.isIPhone16OrDynamicIslandDevice ? { marginTop: '9.5em' } : { marginTop: '9em' };
};

const getWidgetData = (props: BaseWidgetConfig): any => {
  return props.data || null;
};

const ComponentNotFound: ComponentType<{ type: string }> = ({ type }) => (
  <div style={{ padding: '1rem', border: '2px dashed #ccc', textAlign: 'center' }}>
    Component type "{type}" not found
  </div>
);

const DefaultLoadingFallback: React.FC = () => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <div style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>Loading...</div>
  </div>
);

// ================================
// REGISTRY FACTORY
// ================================
export const createComponentRegistry = (components: ComponentDependencies): ComponentRegistry => {
  const LazyCustomPullToRefresh = lazy(() =>
    Promise.resolve({
      default: () => <div>Pull to refresh component not available</div>
    })
  );

  return {
    // Authentication components
    'login': { component: components.ApiLogIn },
    'web-login': { component: components.WebLogIn },
    'forgot-password-mobile': { component: components.ForgotPasswordMobile },
    'forgot-password': { component: components.ForgotPasswordWeb },
    'register': { component: components.RegisterOOBcode },
    'accountCreation-web': { component: components.AccountCreationWeb },
    'RegisterByInvite': { component: components.RegisterByInvite },
    'floating-language-selector': { component: components.FloatingLanguageSelector },

    // Layout components
    'header': { component: components.Header },
    'headerWeb': { component: components.HeaderWeb },
    'headerWebEndUser': { component: components.HeaderWebEndUser },
    'headerWebAdmin': { component: components.HeaderWeb },
    'headerWebApiKeys': { component: components.HeaderWebApiKeys },
    'headerSpacer': {
      component: (props: any) => {
        console.log('[Factory] rendering headerSpacer with props:', props);
        const Comp = components.HeaderSpacer as any;
        return <Comp {...props} />;
      }
    },
    'footer': { component: components.Footer },
    'endUserNav': { component: components.EndUserNav },

    // Map components
    'map': { component: components.EsriMapComponent },

    // Table components
    'sitePaymentsTable': { component: components.SitePaymentsTable },
    'paymentsTable': { component: components.PaymentsTable },
    'table': { component: components.GenericTable },
    'communitiesTable': { component: components.CommunitiesTable },
    'kwickCommunitiesTable': { component: components.KwickCommunitiesTable },
    'membersTable': { component: components.MembersTable },
    'adminUsersInvitedTable': { component: components.AdminUsersInvitedTable },
    'adminUsersActiveTable': { component: components.AdminUsersActiveTable },
    'ApiKeysTable': { component: components.ApiKeysTable },
    'UsersAuditTable': { component: components.UsersAuditTable },

    // List components
    'memberSitesList': { component: () => <components.DetailsList type="memberSites" /> },
    'siteMembersList': { component: () => <components.DetailsList type="siteMembers" /> },
    'communityPaymentsList': { component: () => <components.PaymentsList type="communityPaymentsList" /> },
    // 'CommunityDetailsParameters': { component: () => <components.DetailsList type="communityDetails" data={ getWidgetData } /> },
    'CommunityDetailsParameters': { component: components.DetailsList, data: getWidgetData, type: "communityDetails" },
    'MemberDetailsParameters': { component: () => <components.DetailsList type="memberDetails" /> },
    'SiteDetailsParameters': { component: () => <components.DetailsList type="siteDetails" /> },

    // Stats and card components
    'statsOverview': { component: components.StatsOverview, layoutConfig: calculateMarginTop },
    'statsOverviewWeb': { component: components.StatsOverview, layoutConfig: calculateMarginTop },
    'card': { component: components.InformationCard, layoutConfig: calculateMarginTop },
    'communityStats': { component: components.CommunityStats, layoutConfig: calculateMarginTop },

    // Chart components
    'graphs': { component: components.GraphContainer },
    'areachart': { component: components.GraphContainer },
    'areaChart': { component: components.GraphContainer },
    'combinedGraph': { component: components.GraphContainer },
    'batteryChartMobile': { component: components.BatteryChart },
    'batteryChart': { component: components.BatteryChart },
    'horizontalChart': { component: components.HorizontalWrapper },
    'horizontalChartDouble': { component: components.HorizontalWrapper },
    'parametersDetails': { component: components.ParametersDetails },
    'rowsWrapper': { component: components.RowsWrapper },
    // Special components
    'pull-to-refresh': {
      component: LazyCustomPullToRefresh,
      lazy: true,
      fallback: DefaultLoadingFallback,
    },
  };
};

// ================================
// COMPONENT WRAPPER
// ================================
interface ComponentWrapperProps {
  config: BaseWidgetConfig;
  component: React.ComponentType<any>;
  isLazy?: boolean;
  fallback?: React.ComponentType<any>;
  layoutConfig?: LayoutConfig;
  additionalStyles?: React.CSSProperties;
  enableMemoization?: boolean;
  errorFallback?: React.ComponentType<{ error: Error }>;
}

const ComponentWrapper: React.FC<ComponentWrapperProps> = ({
  config,
  component: Component,
  isLazy = false,
  fallback: LoadingFallback = DefaultLoadingFallback,
  layoutConfig = {},
  additionalStyles = {},
  enableMemoization = true,
  errorFallback,
}) => {

  const WrappedComponent = enableMemoization
    ? getCachedMemoizedComponent(Component, `Widget(${config?.type || 'unknown'})`)
    : Component;

  const containerStyle: React.CSSProperties = {
    ...layoutConfig,
    ...additionalStyles,
  };

  const componentProps = {
    ...config,
    ...(config.data || {}),
    ...(config.widgetsData || {}),
  };

  const componentElement = (
    <div
      className={config.className || ''}
      style={containerStyle}
      onClick={() => config.showInspector?.(config)}
    >
      {(() => {
        if (config.type === 'headerSpacer') {
          console.log('[ComponentWrapper] rendering headerSpacer element with config:', config);
        }
        return <WrappedComponent {...componentProps} />;
      })()}
    </div>
  );

  const widgetInfo = {
    type: config.type,
    id: config.id,
    widgetName: (config as any).widgetName,
    className: config.className,
  };

  if (isLazy) {
    return (
      <ComponentErrorBoundary fallback={errorFallback} widgetInfo={widgetInfo}>
        <Suspense fallback={<LoadingFallback />}>
          {componentElement}
        </Suspense>
      </ComponentErrorBoundary>
    );
  }

  return (
    <ComponentErrorBoundary fallback={errorFallback} widgetInfo={widgetInfo}>
      {componentElement}
    </ComponentErrorBoundary>
  );
};

// ================================
// MAIN FACTORY CLASS
// ================================
export class ComponentFactory {
  private registry: ComponentRegistry;
  private enableMemoization: boolean;

  constructor(registry: ComponentRegistry, enableMemoization = true) {
    this.registry = registry;
    this.enableMemoization = enableMemoization;
  }

  create(
    widgetConfig: BaseWidgetConfig | null,
    additionalStyles: React.CSSProperties = {}
  ): React.ReactElement {

    if (!widgetConfig) {
      return <div key="empty-widget">Empty widget configuration</div>;
    }

    // Allow configs that omit `type` and use `id` as the widget selector (e.g., parametersDetails)
    const inferredType = (widgetConfig as any).type || (this.supports((widgetConfig as any).id) ? ((widgetConfig as any).id as WidgetType) : undefined);

    if (!inferredType) {
      const fallbackKey = generateStableKey(widgetConfig as any);
      return (
        <div key={fallbackKey} className={widgetConfig.className || ''}>
          <ComponentNotFound type={(widgetConfig as any).type || (widgetConfig as any).id || 'unknown'} />
        </div>
      );
    }

    const resolvedConfig = (widgetConfig as any).type ? widgetConfig : ({ ...(widgetConfig as any), type: inferredType } as BaseWidgetConfig);

    const componentEntry = this.registry[inferredType];

    if (!componentEntry) {
      const fallbackKey = generateStableKey(resolvedConfig);
      return (
        <div key={fallbackKey} className={resolvedConfig.className || ''}>
          <ComponentNotFound type={inferredType as string} />
        </div>
      );
    }

    const layoutConfig = componentEntry.layoutConfig?.(resolvedConfig) || {};
    const stableKey = generateStableKey(resolvedConfig);

    return (
      <ComponentWrapper
        key={stableKey}
        config={resolvedConfig}
        component={componentEntry.component}
        isLazy={componentEntry.lazy}
        fallback={componentEntry.fallback}
        layoutConfig={layoutConfig}
        additionalStyles={additionalStyles}
        enableMemoization={this.enableMemoization}
      />
    );
  }

  createFromConfiguration(configuration: any): React.ReactElement {
    if (!configuration?.rows) {
      return <div key="invalid-config">Invalid configuration</div>;
    }

    return (
      <>
        {configuration.rows.map((row: any, rowIndex: number) => (
          <div key={`row-${rowIndex}-${row.id || rowIndex}`}>
            {row?.widgets && row.widgets.length > 0
              ? row.widgets.map((widget: BaseWidgetConfig) => this.create(widget))
              : null}
          </div>
        ))}
      </>
    );
  }

  supports(type: string): boolean {
    return type in this.registry;
  }

  getSupportedTypes(): string[] {
    return Object.keys(this.registry);
  }
}

// ================================
// LEGACY COMPATIBILITY
// ================================
export function Factory(
  widgetConfig: BaseWidgetConfig | null = null,
  additionalStyles: React.CSSProperties = {},
  components?: ComponentDependencies
): React.ReactElement {
  if (!components) {
    return <div>Component dependencies not provided</div>;
  }

  const registry = createComponentRegistry(components);
  const factory = new ComponentFactory(registry);
  return factory.create(widgetConfig, additionalStyles);
}
